import api from '@shared/services/api';
import { UserProfile } from '@shared/types/user';
import { OnboardingProgress, OrganizationInfo } from '../types';

// Onboarding endpoints
export const onboardingEndpoints = {
  getProgress: '/onboarding/progress',
  updateOrganizationType: '/onboarding/progress',
  updateOrganizationInfo: '/onboarding/organization-info',
  updateProfileInfo: '/onboarding/profile-info',
  complete: '/onboarding/complete',
  searchOrganizations: '/onboarding/search-organizations',
} as const;

/**
 * Service for managing onboarding process
 */
class OnboardingService {
  /**
   * Get current onboarding progress
   */
  async getProgress(): Promise<OnboardingProgress> {
    try {
      const response = await api.get(onboardingEndpoints.getProgress);
      return response.data;
    } catch (error) {
      console.error('Error fetching onboarding progress:', error);
      throw error;
    }
  }

  /**
   * Update organization type
   * @param organizationType - Type of organization (individual or organization)
   * @param receiveNotifications - Whether to receive notifications
   */
  async updateOrganizationType(organizationType: string, receiveNotifications: boolean): Promise<OnboardingProgress> {
    try {
      const response = await api.post(onboardingEndpoints.updateOrganizationType, {
        currentStep: 1,
        organizationType,
        receiveNotifications
      });
      return response.data;
    } catch (error) {
      console.error('Error updating organization type:', error);
      throw error;
    }
  }

  /**
   * Update organization information
   * @param organizationInfo - Organization information
   */
  async updateOrganizationInfo(organizationInfo: OrganizationInfo): Promise<OnboardingProgress> {
    try {
      const response = await api.post(onboardingEndpoints.updateOrganizationInfo, {
        currentStep: 2,
        organizationInfo
      });
      return response.data;
    } catch (error) {
      console.error('Error updating organization info:', error);
      throw error;
    }
  }

  /**
   * Update profile information
   * @param profileInfo - User profile information
   */
  async updateProfileInfo(profileInfo: Partial<UserProfile>): Promise<OnboardingProgress> {
    try {
      const response = await api.post(onboardingEndpoints.updateProfileInfo, {
        currentStep: 3,
        profileInfo
      });
      return response.data;
    } catch (error) {
      console.error('Error updating profile info:', error);
      throw error;
    }
  }

  /**
   * Complete onboarding process
   */
  async completeOnboarding(): Promise<OnboardingProgress> {
    try {
      const response = await api.post(onboardingEndpoints.complete);
      return response.data;
    } catch (error) {
      console.error('Error completing onboarding:', error);
      throw error;
    }
  }

  /**
   * Search for existing organizations by name
   * @param name - Organization name to search for
   */
  async searchOrganizations(name: string): Promise<any[]> {
    try {
      const response = await api.get(`${onboardingEndpoints.searchOrganizations}?name=${encodeURIComponent(name)}`);
      return response.data;
    } catch (error) {
      console.error('Error searching organizations:', error);
      return [];
    }
  }
}

export const onboardingService = new OnboardingService();
